import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, ShieldCheck, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * ResetPasswordPage
 * -----------------
 * Public landing page for the link sent by /forgot-password.
 * Reads the single-use token from `?token=...`, posts to
 * /api/auth/reset-password with {token, new_password}, then
 * routes the user back to /login on success.
 *
 * The strength rules mirror the server-side gate in
 * routes/auth.py::_validate_password_strength so users get
 * real-time validation feedback.
 *
 * Design notes
 *   • If the URL has no token at all we render the generic
 *     "invalid or expired" state immediately — no API call.
 *   • Token validation happens server-side on submit; we don't
 *     pre-validate via a separate GET so the token is only
 *     observed once (consumed on submit), preventing accidental
 *     consumption from background fetches.
 */
const RULES = [
  { id: 'len', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'alpha', label: 'Contains a letter', test: (p) => /[a-zA-Z]/.test(p) },
  { id: 'mixed', label: 'Contains a number or symbol', test: (p) => /[^a-zA-Z]/.test(p) },
];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errored, setErrored] = useState(!token);

  useEffect(() => {
    if (!token) setErrored(true);
  }, [token]);

  const ruleResults = RULES.map((r) => ({ ...r, ok: r.test(newPassword) }));
  const allRulesPass = ruleResults.every((r) => r.ok);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = !submitting && !errored && allRulesPass && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      toast.success('Password reset. Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (
        Array.isArray(detail)
          ? detail.map(e2 => e2.msg || JSON.stringify(e2)).join('; ')
          : (typeof detail === 'string' ? detail : 'Reset link is invalid or has expired.')
      );
      toast.error(msg);
      // 400 = expired/invalid token. Lock the form so the user
      // can't keep retrying with the same dead token.
      if (err?.response?.status === 400) setErrored(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4"
      data-testid="reset-password-page"
    >
      <Card className="w-full max-w-md bg-white border border-slate-200 backdrop-blur shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className={
              'h-12 w-12 rounded-full flex items-center justify-center '
              + (errored ? 'bg-rose-50' : 'bg-teal-50')
            }>
              {errored ? <AlertCircle className="h-6 w-6 text-rose-600" /> : <ShieldCheck className="h-6 w-6 text-teal-600" />}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            {errored ? 'Reset link is invalid' : 'Choose a new password'}
          </CardTitle>
          <CardDescription>
            {errored
              ? 'This password reset link is missing, expired, or already used. Request a fresh one to continue.'
              : 'Pick something you\'ll remember — you\'ll use it the next time you sign in.'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {errored ? (
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full h-11 bg-teal-600 hover:bg-teal-700"
                data-testid="reset-password-request-new-btn"
              >
                Request a new reset link
              </Button>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-teal-700 hover:text-teal-900"
                  data-testid="reset-password-back-to-login"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">New password</label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type={show ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    data-testid="reset-new-password-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label="Toggle password visibility"
                  >
                    {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Confirm new password</label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type={show ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-rose-600 mt-1">Passwords don't match.</p>
                )}
              </div>

              <ul className="text-xs space-y-1 text-slate-600" data-testid="reset-password-rules-list">
                {ruleResults.map((r) => (
                  <li
                    key={r.id}
                    className={r.ok ? 'text-teal-700' : 'text-slate-500'}
                    data-testid={`reset-password-rule-${r.id}`}
                  >
                    <span aria-hidden>{r.ok ? '✓' : '○'}</span> {r.label}
                  </li>
                ))}
              </ul>

              <Button
                type="submit"
                className="w-full h-11 bg-teal-600 hover:bg-teal-700"
                disabled={!canSubmit}
                data-testid="reset-password-submit-btn"
              >
                {submitting
                  ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  : 'Set new password & sign in'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
